import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from '@phosphor-icons/react';
import './TimePicker.css';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function TimePicker({
  value = '',
  onChange,
  disabled = false,
  error = false,
  name,
  className = '',
  placeholder = '00:00',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const hourListRef = useRef(null);
  const minuteListRef = useRef(null);
  const inputRef = useRef(null);

  // Portal positioning — the dropdown escapes overflow-clipped modal bodies
  // and flips above the input when there's no room below (same mechanic as
  // DatePicker).
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < dropdownHeight
      ? rect.top - dropdownHeight - 4
      : rect.bottom + 4;
    setDropdownPos({ top, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const [hour, minute] = value ? value.split(':') : ['', ''];

  const emitChange = useCallback((val) => {
    const syntheticEvent = { target: { name, value: val } };
    onChange?.(syntheticEvent);
  }, [name, onChange]);

  const handleHourSelect = (h) => {
    const newMinute = minute || '00';
    const newVal = `${h}:${newMinute}`;
    setInputValue(newVal);
    emitChange(newVal);
  };

  const handleMinuteSelect = (m) => {
    const newHour = hour || '09';
    const newVal = `${newHour}:${m}`;
    setInputValue(newVal);
    emitChange(newVal);
  };

  const handleInputChange = (e) => {
    let raw = e.target.value.replace(/[^\d:]/g, '');

    if (raw.length === 2 && !raw.includes(':') && inputValue.length < raw.length) {
      raw = raw + ':';
    }

    if (raw.length > 5) raw = raw.slice(0, 5);

    setInputValue(raw);

    const match = raw.match(/^(\d{2}):(\d{2})$/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        emitChange(raw);
      }
    }
  };

  const handleInputBlur = () => {
    const match = inputValue.match(/^(\d{1,2}):?(\d{0,2})$/);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = match[2] ? parseInt(match[2], 10) : 0;
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        setInputValue(formatted);
        emitChange(formatted);
        return;
      }
    }
    if (inputValue && !inputValue.match(/^\d{2}:\d{2}$/)) {
      setInputValue(value);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setInputValue(value);
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      if (hour && hourListRef.current) {
        const el = hourListRef.current.querySelector(`[data-value="${hour}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
      if (minute && minuteListRef.current) {
        const el = minuteListRef.current.querySelector(`[data-value="${minute}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    });
  }, [isOpen, hour, minute]);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) updateDropdownPosition();
      return !prev;
    });
  };

  return (
    <div
      className={`ck-timepicker ${isOpen ? 'ck-timepicker--open' : ''} ${error ? 'ck-timepicker--error' : ''} ${disabled ? 'ck-timepicker--disabled' : ''} ${className}`}
      ref={containerRef}
    >
      <div className="ck-timepicker__input-row">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="ck-timepicker__input"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          dir="ltr"
        />
        <button
          type="button"
          className="ck-timepicker__toggle"
          onClick={toggleDropdown}
          disabled={disabled}
          tabIndex={-1}
          aria-label="בחירת שעה"
          aria-expanded={isOpen}
        >
          <Clock size={16} weight={isOpen ? 'fill' : 'regular'} />
        </button>
      </div>

      {isOpen && createPortal(
        <>
          <div className="ck-timepicker__backdrop" onClick={() => setIsOpen(false)} />
          <div
            ref={dropdownRef}
            className="ck-timepicker__dropdown ck-timepicker__dropdown--portal"
            style={{ top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width }}
          >
            <div className="ck-timepicker__mobile-header">
              <span>בחירת שעה</span>
              <button type="button" className="ck-timepicker__done" onClick={() => setIsOpen(false)}>
                סיום
              </button>
            </div>
            <div className="ck-timepicker__columns">
              <div className="ck-timepicker__column">
                <div className="ck-timepicker__column-header">שעה</div>
                <div className="ck-timepicker__list" ref={hourListRef} role="listbox" aria-label="שעה">
                  {HOURS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      role="option"
                      data-value={h}
                      aria-selected={h === hour}
                      className={`ck-timepicker__option ${h === hour ? 'ck-timepicker__option--selected' : ''}`}
                      onClick={() => handleHourSelect(h)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ck-timepicker__separator" />
              <div className="ck-timepicker__column">
                <div className="ck-timepicker__column-header">דקות</div>
                <div className="ck-timepicker__list" ref={minuteListRef} role="listbox" aria-label="דקות">
                  {MINUTES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="option"
                      data-value={m}
                      aria-selected={m === minute}
                      className={`ck-timepicker__option ${m === minute ? 'ck-timepicker__option--selected' : ''}`}
                      onClick={() => handleMinuteSelect(m)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default TimePicker;
